import {
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
  signOut as firebaseSignOut,
  onIdTokenChanged,
  type User,
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { auth } from './firebase';

const SIGN_IN_EMAIL_KEY = 'w6:signInEmail';

const actionCodeSettings = {
  url: `${window.location.origin}/auth/callback`,
  handleCodeInApp: true,
};

export async function requestSignInLink(email: string): Promise<void> {
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  window.localStorage.setItem(SIGN_IN_EMAIL_KEY, email);
}

export function isMagicLink(href: string): boolean {
  return isSignInWithEmailLink(auth, href);
}

export async function completeSignIn(href: string): Promise<User> {
  const email = window.localStorage.getItem(SIGN_IN_EMAIL_KEY);
  if (!email) {
    throw new Error('Ingen email hittad — börja om från /login');
  }
  const result = await signInWithEmailLink(auth, email, href);
  window.localStorage.removeItem(SIGN_IN_EMAIL_KEY);
  return result.user;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

export type AuthClaims = {
  uid: string;
  email: string | null;
  name: string;
  role: 'admin' | 'member';
};

export function useAuth(): { user: AuthClaims | null; loading: boolean } {
  const [user, setUser] = useState<AuthClaims | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      const tokenResult = await firebaseUser.getIdTokenResult();
      const claims = tokenResult.claims as { role?: string; name?: string };
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: claims.name ?? firebaseUser.displayName ?? '',
        role: claims.role === 'admin' ? 'admin' : 'member',
      });
      setLoading(false);
    });
  }, []);

  return { user, loading };
}
